"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
const reimbursements = [{
  procedure: "Microdiscectomy",
  code: "B03C",
  amount: "Up to €8,558",
  note: "Minimally invasive spinal intervention, day case rate"
}, {
  procedure: "Nucleoplasty",
  code: "B03C",
  amount: "Up to €8,558",
  note: "Radiofrequency spinal surgical procedure, day case rate"
}, {
  procedure: "Vertebroplasty",
  code: "B03C",
  amount: "Up to €8,558",
  note: "Minimally invasive spinal intervention, day case rate"
}, {
  procedure: "Pain management procedures",
  code: "I68B",
  amount: "Up to €874",
  note: "Non-surgical spinal disorder, day case rate"
}, {
  procedure: "Outpatient consultation",
  code: "Outpatient",
  amount: "Up to €194",
  note: "Per consultation day"
}];
const eligible = ["You have been living in Ireland for at least 1 year", "You are eligible for the same treatment in the Irish public health system", "You have a referral from a GP or public consultant in Ireland", "You are on a public waiting list for spine surgery or pain management", "You pay for treatment yourself — insurance payments are not eligible"];
const notCovered = ["Flights to and from Lithuania", "Hotel accommodation before or after surgery", "Transport between hotel, clinic and rehabilitation center", "Meals and daily living expenses while abroad", "Translation costs for documents not in English", "Fees paid to third-party medical tourism agencies"];
const steps = [{
  id: 1,
  title: "Get a referral from your GP",
  body: "Ask your GP for a referral letter confirming you are on a public waiting list in Ireland for spine surgery or pain management. The referral must be dated before your consultation at Clinic Agatas. This is a strict HSE requirement."
}, {
  id: 2,
  title: "Submit a prior notification to HSE",
  body: "Before you travel, we recommend submitting a prior notification to the HSE Cross-Border Directive office in Kilkenny. HSE responds within 6 weeks to confirm your eligibility and give an indication of your reimbursement amount. Our specialist helps you complete this step."
}, {
  id: 3,
  title: "Online consultation with your surgeon",
  body: "Send your MRI scans and have a remote consultation with your neurosurgeon. He confirms whether surgery is appropriate and outlines your treatment plan. This is not counted as your mandatory pre-operative consultation."
}, {
  id: 4,
  title: "In-person consultation the day before surgery",
  body: "You arrive in Kaunas and meet your neurosurgeon in person the day before your procedure. This in-person consultation is a mandatory HSE requirement. Surgery is never performed on the day of arrival."
}, {
  id: 5,
  title: "Surgery at Clinic Agatas",
  body: "Your procedure is performed at our licensed day surgery centre. You pay the clinic directly. Keep your invoice and receipt — these are required documents for your HSE claim."
}, {
  id: 6,
  title: "Apply for reimbursement",
  body: "After treatment, we provide your Discharge Summary and Operative Report with the correct DRG code. You submit the completed HSE Healthcare Costs Application Form along with your referral letter, invoice and proof of payment. HSE processes your claim within 30 working days."
}];
const documents = [{
  title: "GP referral letter",
  detail: "Must confirm you are on a public waiting list. Must be dated before your consultation at Clinic Agatas. Must include your name, address, date of birth, diagnosis and GP signature."
}, {
  title: "HSE Healthcare Costs Application Form",
  detail: "Available from hse.ie. Section 4 must be completed by your neurosurgeon at Clinic Agatas. Must be in English.",
  link: "https://assets.hse.ie/media/documents/CBD-Healthcare-costs-application-form.pdf",
  linkLabel: "Download form"
}, {
  title: "Invoice and receipt from Clinic Agatas",
  detail: "Must include your name and address, clinic name and address, procedure name, date of treatment and amount paid with full breakdown."
}, {
  title: "Proof you paid directly",
  detail: "Bank statement or card receipt showing payment to Clinic Agatas. The payee name and amount must match your invoice exactly."
}, {
  title: "Prior notification confirmation (recommended)",
  detail: "Not mandatory but strongly recommended. Confirms your eligibility and indicative reimbursement amount before you travel.",
  link: "https://assets.hse.ie/media/documents/CBD-Prior-notification-form.pdf",
  linkLabel: "Download form"
}];
const warnings = ["Consultation and surgery on the same day — HSE will reject the claim", "Referral letter dated after your consultation — HSE will reject the claim", "Incorrect DRG code on your invoice — reimbursement will be reduced or refused", "Pro-Forma Invoice not completed in English — certified translation required at your cost", "Payment made by insurance rather than directly by you — not eligible for reimbursement", "No evidence of being on an Irish public waiting list — not eligible", "Travel and accommodation costs included in claim — not reimbursable under CBD"];
function Hero() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section className="relative w-full bg-[#264256] overflow-hidden min-h-[40vh] md:min-h-[50vh] flex items-center">
      <div className="container mx-auto px-6 max-w-7xl relative z-10 py-10 md:py-16">
        <div ref={ref} className={cn("flex flex-col items-center text-center gap-4 md:gap-6 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          <span className="font-default text-[12px] text-[#65d19d] uppercase tracking-widest font-semibold">EU Cross-Border Healthcare Directive</span>
          <h1 className="font-default font-bold text-4xl md:text-[60px] leading-[1.1] text-white max-w-4xl">Your legal right to treatment abroad.</h1>
          <p className="font-default text-white/80 text-base md:text-xl leading-relaxed max-w-2xl">As an Irish public patient, you have the right to seek treatment at Clinic Agatas in Lithuania and claim reimbursement from the HSE. We help you through every step of the process.</p>
          <div className="flex flex-col sm:flex-row gap-4 pt-2">
            <Button asChild className="rounded-lg bg-[#65d19d] text-[#264256] hover:bg-[#65d19d]/90 h-12 md:h-14 px-8 md:px-10 text-sm uppercase tracking-widest font-bold font-default transition-all duration-300 shadow-lg">
              <Link to="/#request-your-consultation">Check Your Eligibility</Link>
            </Button>
            <Button asChild className="rounded-lg bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#264256] h-12 md:h-14 px-8 md:px-10 text-sm uppercase tracking-widest font-bold font-default transition-all duration-300">
              <a href="#how-it-works">See how it works</a>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}
function EligibilitySection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section className="bg-background py-8 md:py-20 border-b border-[#f1f3f5]">
      <div className="container mx-auto px-6 max-w-7xl">
        <div ref={ref} className={cn("transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="flex flex-col lg:flex-row gap-10 md:gap-16 items-start">
            <div className="w-full lg:w-[50%] flex flex-col gap-5">
              <h2 className="font-default font-bold text-2xl md:text-4xl text-[#264256] leading-tight">Who can apply</h2>
              <p className="font-default text-muted-foreground text-base leading-relaxed">The EU Cross-Border Healthcare Directive is available to Irish public patients who meet all of the following criteria. If you are unsure whether you qualify, contact us and we will assess your situation at no cost.</p>
              <ul className="flex flex-col gap-3">
                {eligible.map((item, i) => <li key={i} className="flex items-start gap-3">
                    <div className="bg-[#eefaf4] rounded-full p-1 shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-[#65d19d]" strokeWidth={3} />
                    </div>
                    <span className="font-default text-[#264256] text-sm leading-relaxed">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="w-full lg:w-[50%] flex flex-col gap-5">
              <h2 className="font-default font-bold text-2xl md:text-4xl text-[#264256] leading-tight">What is not covered</h2>
              <p className="font-default text-muted-foreground text-base leading-relaxed">The CBD covers the cost of medical treatment only. The following are never reimbursable regardless of circumstances.</p>
              <ul className="flex flex-col gap-3 mt-2">
                {notCovered.map((item, i) => <li key={i} className="flex items-start gap-3">
                    <div className="bg-red-50 rounded-full p-1 shrink-0 mt-0.5">
                      <X className="w-3.5 h-3.5 text-red-400" strokeWidth={3} />
                    </div>
                    <span className="font-default text-[#264256] text-sm leading-relaxed">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function ReimbursementSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section className="bg-[#eefaf4] py-8 md:py-20 border-b border-[#a9e5c9]">
      <div className="container mx-auto px-6 max-w-7xl">
        <div ref={ref} className={cn("transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="text-center mb-8 md:mb-12">
            <h2 className="font-default font-bold text-2xl md:text-4xl text-[#264256] mb-3">How much can you claim back?</h2>
            <p className="font-default text-muted-foreground text-base max-w-2xl mx-auto">HSE reimburses the lesser of two amounts: what you paid at Clinic Agatas, or the Irish public system rate for the same procedure. All rates are from the official HSE day case price list effective 1 July 2025.</p>
          </div>
          <div className="bg-white rounded-lg border border-[#a9e5c9] overflow-hidden shadow-sm mb-6">
            <div className="bg-[#264256] px-6 py-4">
              <h3 className="font-default font-bold text-white text-base uppercase tracking-wider">HSE Day Case Reimbursement Rates</h3>
            </div>
            <div className="divide-y divide-[#eefaf4]">
              {reimbursements.map((item, i) => <div key={i} className="flex flex-col md:flex-row md:items-center justify-between px-6 py-4 gap-2">
                  <div className="flex flex-col gap-1">
                    <span className="font-default font-bold text-[#264256] text-base">{item.procedure}</span>
                    <span className="font-default text-muted-foreground text-xs">DRG {item.code} — {item.note}</span>
                  </div>
                  <span className="font-default font-bold text-[#264256] text-2xl shrink-0">{item.amount}</span>
                </div>)}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-[#a9e5c9] p-6">
            <h3 className="font-default font-bold text-[#264256] text-sm uppercase tracking-wider mb-3">Important: Clinic Agatas is a licensed day surgery centre</h3>
            <p className="font-default text-muted-foreground text-sm leading-relaxed">All procedures at Clinic Agatas are classified as day case under Lithuanian healthcare regulations. HSE reimbursement is calculated at the Irish day case rate. We provide overnight medical supervision as standard post-operative care, but your formal discharge is same-day.</p>
          </div>
        </div>
      </div>
    </section>;
}
function RehabilitationSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section className="bg-background py-8 md:py-20 border-b border-[#f1f3f5]">
      <div className="container mx-auto px-6 max-w-7xl">
        <div ref={ref} className={cn("transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="flex flex-col lg:flex-row gap-10 md:gap-16 items-start">
            <div className="w-full lg:w-[55%] flex flex-col gap-5">
              <span className="font-default text-[12px] text-[#65d19d] uppercase tracking-widest font-semibold">Rehabilitation</span>
              <h2 className="font-default font-bold text-2xl md:text-4xl text-[#264256] leading-tight">Can rehabilitation at Egle Rehabilitation center be reimbursed?</h2>
              <p className="font-default text-muted-foreground text-base leading-relaxed">HSE assesses rehabilitation reimbursement individually, based on your procedure and clinical condition. Egle Rehabilitation center in Birstonas is a licensed medical rehabilitation facility that meets the requirements for CBD reimbursement consideration.</p>
              <p className="font-default text-muted-foreground text-base leading-relaxed">Before you travel, we strongly recommend submitting a prior notification that includes rehabilitation. HSE will confirm what is covered in your specific case and at what rate, before you commit to anything.</p>
              <div className="bg-[#eefaf4] rounded-lg border border-[#a9e5c9] p-6">
                <h3 className="font-default font-bold text-[#264256] text-sm uppercase tracking-wider mb-3">What prior notification tells you</h3>
                <ul className="flex flex-col gap-2">
                  {["Whether your rehabilitation qualifies for reimbursement", "Which specific procedures at Egle Rehabilitation center are covered", "The indicative reimbursement amount for your rehabilitation", "Any conditions or limitations that apply to your case"].map((item, i) => <li key={i} className="flex items-start gap-3">
                      <div className="bg-white rounded-full p-1 shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-[#65d19d]" strokeWidth={3} />
                      </div>
                      <span className="font-default text-[#264256] text-sm leading-relaxed">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
            <div className="w-full lg:w-[45%] flex flex-col gap-4 lg:pt-16">
              <div className="bg-[#264256] rounded-lg p-6 flex flex-col gap-4">
                <h3 className="font-default font-bold text-white text-base">Our recommendation</h3>
                <p className="font-default text-white/80 text-sm leading-relaxed">Include rehabilitation in your prior notification application before you travel. This is the only way to know in advance what HSE will cover in your individual case. We help you prepare and submit this application as part of our standard process.</p>
                <Button asChild className="rounded-lg bg-[#65d19d] text-[#264256] hover:bg-[#65d19d]/90 h-auto min-h-12 py-3 px-6 text-sm uppercase tracking-normal md:tracking-widest font-bold font-default transition-all duration-300 w-full md:w-fit text-center whitespace-normal leading-snug">
                  <Link to="/#request-your-consultation">Ask us about prior notification</Link>
                </Button>
              </div>
              <div className="bg-[#f8fbf9] rounded-lg border border-[#f1f3f5] p-5">
                <p className="font-default text-muted-foreground text-xs leading-relaxed">Accommodation and meals at Egle Rehabilitation center are not reimbursable under the CBD regardless of prior notification outcome.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function StepsSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.05
  });
  return <section id="how-it-works" className="bg-[#264256] py-8 md:py-20">
      <div className="container mx-auto px-6 max-w-5xl">
        <div ref={ref} className={cn("transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="text-center mb-10 md:mb-16">
            <h2 className="font-default font-bold text-2xl md:text-4xl text-white mb-3">How the process works</h2>
            <p className="font-default text-white/70 text-base max-w-2xl mx-auto">Six steps from your first contact to receiving your HSE reimbursement.</p>
          </div>
          <div className="flex flex-col gap-6">
            {steps.map((step, i) => <div key={i} className="flex gap-4 md:gap-6 items-start">
                <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-[#65d19d] flex items-center justify-center shrink-0 mt-0.5">
                  <span className="font-default font-bold text-[#264256] text-sm md:text-base">{step.id}</span>
                </div>
                <div className="flex flex-col gap-2 pt-1">
                  <h3 className="font-default font-bold text-white text-base">{step.title}</h3>
                  <p className="font-default text-white/75 text-sm leading-relaxed">{step.body}</p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}
function DocumentsSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section className="bg-background py-8 md:py-20">
      <div className="container mx-auto px-6 max-w-7xl">
        <div ref={ref} className={cn("transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="flex flex-col lg:flex-row gap-10 md:gap-16">
            <div className="w-full lg:w-[55%] flex flex-col gap-5">
              <h2 className="font-default font-bold text-2xl md:text-4xl text-[#264256] leading-tight">Documents you will need</h2>
              <p className="font-default text-muted-foreground text-base leading-relaxed">Your HSE application will be rejected if any document is missing or incorrectly completed. We provide all clinical documentation from Clinic Agatas. You are responsible for the referral letter from your GP and proof of payment.</p>
              <div className="flex flex-col gap-4">
                {documents.map((doc, i) => <div key={i} className="bg-[#eefaf4] rounded-lg border border-[#a9e5c9] p-5 flex flex-col gap-2">
                    <div className="flex items-start gap-3">
                      <div className="bg-white rounded-full p-1 shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5 text-[#65d19d]" strokeWidth={3} />
                      </div>
                      <h3 className="font-default font-bold text-[#264256] text-sm">{doc.title}</h3>
                    </div>
                    <p className="font-default text-muted-foreground text-sm leading-relaxed pl-7">{doc.detail}</p>
                    {doc.link && <a href={doc.link} target="_blank" rel="noopener noreferrer" className="pl-7 font-default text-[#264256] text-xs font-bold uppercase tracking-wider hover:underline">
                        {doc.linkLabel} →
                      </a>}
                  </div>)}
              </div>
            </div>
            <div className="w-full lg:w-[45%] flex flex-col gap-5">
              <h2 className="font-default font-bold text-2xl md:text-4xl text-[#264256] leading-tight">Why claims get rejected</h2>
              <p className="font-default text-muted-foreground text-base leading-relaxed">These are the most common reasons HSE rejects or reduces reimbursement claims. We structure your treatment at Clinic Agatas to avoid every one of them.</p>
              <div className="flex flex-col gap-3">
                {warnings.map((item, i) => <div key={i} className="flex items-start gap-3 bg-white rounded-lg border border-[#f1f3f5] p-4 shadow-sm">
                    <div className="bg-red-50 rounded-full p-1 shrink-0 mt-0.5">
                      <X className="w-3.5 h-3.5 text-red-400" strokeWidth={3} />
                    </div>
                    <span className="font-default text-[#264256] text-sm leading-relaxed">{item}</span>
                  </div>)}
              </div>
              <div className="bg-[#264256] rounded-lg p-6 mt-2">
                <p className="font-default text-white/90 text-sm leading-relaxed">HSE processes claims within 30 working days of receiving all completed documents. Payments are made directly to the patient.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
export default function Authority() {
  return <>
      <Hero />
      <EligibilitySection />
      <ReimbursementSection />
      <RehabilitationSection />
      <StepsSection />
      <DocumentsSection />
      <section className="bg-background py-10 md:py-16">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="bg-[#264256] rounded-2xl px-6 md:px-10 py-10 md:py-14 text-center flex flex-col items-center gap-6 md:gap-8">
            <div className="flex flex-col gap-3 md:gap-4">
              <h2 className="font-default font-bold text-2xl md:text-4xl text-white leading-tight">Not sure if you qualify?</h2>
              <p className="font-default text-white/80 text-sm md:text-base leading-relaxed max-w-xl mx-auto">Contact us and we will assess your eligibility at no cost. If you qualify, our specialist will guide you through the prior notification process before you travel.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild className="rounded-lg bg-[#65d19d] text-[#264256] hover:bg-[#65d19d]/90 h-12 md:h-14 px-8 md:px-10 text-sm uppercase tracking-widest font-bold font-default transition-all duration-300 shadow-lg">
                <Link to="/#request-your-consultation">Contact Us</Link>
              </Button>
              <Button asChild className="rounded-lg bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#264256] h-12 md:h-14 px-8 md:px-10 text-sm uppercase tracking-widest font-bold font-default transition-all duration-300">
                <a href="https://www2.hse.ie/services/schemes-allowances/cross-border-directive/" target="_blank" rel="noopener noreferrer">HSE Official Info</a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>;
}